import React, { useState, useEffect } from 'react';
import OnceFooter from "./../common/Footer/OnceFooter";
import { useParams, Link } from 'react-router-dom';
import api from './../../utils/api';
import { Modal, Button, Row, Col, Nav, Tab } from 'react-bootstrap';
import { numberFormat, inputKeyPressOnlyNumber, transformFieldWithLang } from './../../utils/functions';
import { toast } from 'react-toastify';
import moment from "moment";
import 'moment/locale/th'
import _ from "lodash";
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next'
import ChangeLanguageDropdown from '../common/ChangeLanguageDropdown';
import BalancePayCard from './BalancePayCard';

export default function BookingStatus() {
  const { id, code } = useParams();
  const [booking, setBooking] = useState({});
  const { hotel, room, campZoneItem, campZone, roomFlexible } = booking
  const [filterCode, setFilterCode] = useState(code || '');
  const [filterTel, setFilterTel] = useState('');

  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'th'

  const endDate = new Date(booking?.end_date)
  const endDateStr = endDate.setDate(endDate.getDate() + 1)

  const [addons, setAddons] = useState([]);
  const [items, setItems] = useState([]);

  const addonsText = addons.map((x) => { return `${transformFieldWithLang(x?.addons_type, 'name', lang)} (${x?.qty})` })
  const addonsTextStr = (addonsText || []).join(', ')
  const addonsSum = _.sumBy(addons || [], (x) => { return Number(x.total_price || 0) }) || 0

  const bookingDatesStr = (booking?.room_id || booking?.room_flexible_id) ? items.map((b) => `${moment(b.date).format('DD/MM/YYYY')} (${numberFormat(b.unit_total_price, true)})`).join(', ') :
    items.map((b) => `${moment(b.date).format('DD/MM/YYYY')} (${numberFormat((Number(b.unit_total_price || 0) + Number(b.extra_price || 0)), true)})`).join(', ')

  const [policies, setPolicies] = useState([]);
  const [foods, setFoods] = useState([]);
  const [attractions, setAttractions] = useState([]);

  const fetchBookingData = () => {
    api.request(`bookings/findonebooking`, (data) => {
      setBooking(data || {})
      setItems(data?.booking_items || [])

      if (data.booking_addons && data.booking_addons.length) {
        setAddons(data.booking_addons.map((a) => {
          return {
            ...a,
            addons_id: a.addons_id,
            total_price: a.total_price || 0,
            qty: a.qty || 0
          }
        }))
      }

      if (!!data?.id) {
        ReactGA.send({ hitType: "pageview", page: `${data?.hotel?.code}/bookingstatus`, title: `${data?.hotel?.name_th}-${data?.room_id ? data?.room?.name_th : 'camping'} BookingStatus` });

        ReactGA.event({
          category: "BookingProcess",
          action: `${data?.hotel?.code}_${data?.room_id ? data?.room?.room_no : 'camping'}_booked`,
          label: `Booking Status Success - ${data?.hotel?.code}-${data?.room_id ? data?.room?.room_no : 'camping'}`,
        });
      }

      if (data?.hotel?.show_hotel_info_to_customer) {
        fetchRecommentPlaces(data?.hotel_id);
        fetchPolicies(data?.hotel_id, (data?.room_id || null), !!data?.is_walkin, data?.close_zone_type === 'all');
      }
    }, {
      id: id,
      booking_no: filterCode,
      tel: filterTel
    })
  }

  const fetchPolicies = (_hotel_id, _room_id = null, with_walkin = false, with_close_all = false) => {
    if (!_hotel_id) return;

    let params = {
      hotel_id: _hotel_id,
      enabled: true,
      types: ['hotel_policy']
    }

    if (with_walkin) {
      params.types.push('camping_policy')
    }

    if (with_close_all) {
      params.types.push('closeall_policy')
    }

    if (!!_room_id) {
      params.types.push('room_policy')
      params.with_room_ids = [_room_id]
    }

    api.request(`hotels/hotel-info/all`, (data) => {
      if (!data || data.length === 0) {
        setPolicies([]);
        return;
      }

      setPolicies(data);
    }, params)
  }

  const fetchRecommentPlaces = (_hotel_id) => {
    if (!_hotel_id) return;

    api.request(`hotels/recommend-nearby-places/all`, (data) => {
      if (!data || data.length === 0) {
        setFoods([]);
        setAttractions([]);
        return;
      }

      const foodsData = data.filter((d) => d.type === 'food') || [];
      const attractionsData = data.filter((d) => d.type === 'attraction') || [];

      setFoods(foodsData);
      setAttractions(attractionsData);
    }, {
      hotel_id: _hotel_id,
      enabled: true,
    })
  }

  useEffect(() => {
    if (!!id) {
      fetchBookingData()
    }
  }, [id, filterCode, filterTel])

  const warningMsg = booking?.payment_state === 'complete' ? t('booking_notify_info_complete') : t('booking_notify_info_wait_payment')
  const outstanding = (Number(booking.net_total || 0) - Number(booking.paid_amount || 0))
  // const outstanding = booking.payment_type === 'full' ? 0 : (Number(booking.net_total || 0) - Number(booking.paid_amount || 0))

  // ignore canceled rows; only a customer_paid deposit counts as money received
  const roomDeposit = (booking.room_deposits || []).find((d) => d.state !== 'canceled') || null
  const roomDepositPaid = roomDeposit?.state === 'customer_paid' ? Number(roomDeposit.deposit_amount || 0) : 0

  const isCloseAll = booking.close_zone_type === 'all'
  const areaName = isCloseAll ? t('booking_close_all') : !!campZoneItem ? `[${transformFieldWithLang(campZone, 'name', lang)}] : ${transformFieldWithLang(campZoneItem, 'name', lang)}` : (transformFieldWithLang(room, 'name', lang) || transformFieldWithLang(roomFlexible, 'name', lang) || transformFieldWithLang(booking?.campType, 'name', lang) || t('camping_booking_title'))

  const isCamping = booking.is_walkin

  let roomItemNames = ''
  const bookingRoomItems = booking.booking_room_items || []

  if (bookingRoomItems > 0) {
    const names = (bookingRoomItems.filter((x) => !!x.item_id) || []).map((b) => { return transformFieldWithLang(b.roomItem, 'name', lang) })
    roomItemNames = names.length > 0 ? names.join(', ') : ''
  }

  const additionalPrice = Number(booking.additional_guest_total_price || 0)
  const addtiionalPet = booking.booking_additional_pets && booking.booking_additional_pets.length > 0 ? booking.booking_additional_pets[0] : null;

  const hotelPolicy = policies.find((p) => p.type === 'hotel_policy') || null;
  const campingPolicy = policies.find((p) => p.type === 'camping_policy') || null;
  const closeAllPolicy = policies.find((p) => p.type === 'closeall_policy') || null;
  const roomPolicy = policies.find((p) => p.type === 'room_policy') || null;

  const [placeHtmlDataForShow, setPlaceHtmlDataForShow] = useState(null);

  const bookingOverviewSection = () => {
    return (
      <Row>
        {!!booking.receipt_file_path && <Col xs={12} className="mb-3">

          <Button variant="success" size="sm" onClick={() => {
            window.open(booking.receipt_file_path, '_blank')
          }}>{t('download')} {booking.payment_state === 'complete' ? t('receipt_doc') : t('confirmation_doc')}</Button>
        </Col>}
        <Col xs={12} md={6}>
          <Row>
            <Col xs={12}>
              <span className="title">{t('room')}/{t('zone')} : </span>
              <span className="detail">{areaName}</span>
            </Col>
            {!!roomFlexible && <Col xs={12}>
              <span className="title">{t('booked_rooms_qty')} : </span>
              <span className="detail">{booking.booking_room_items?.length} {t('room_unit')}</span>
              <span className="text-muted ms-2">{roomItemNames}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('stay_date')} : </span>
              <span className="detail">{moment(booking?.start_date).format('DD/MM/YYYY')} - {moment(endDateStr).format('DD/MM/YYYY')} ({booking?.day_qty} {t('nights')})</span>
            </Col>
            {!isCamping && <Col xs={12}>
              <span className="title">{t('guest')} : </span>
              <span className="detail">{t('adult')} {numberFormat(booking.guest_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.guest_baby_qty, true)} {t('persons')}</span>
            </Col>}
            {!isCamping && additionalPrice > 0 && <Col xs={12}>
              <span className="title">{t('guest')} ({t('additional')}) : </span>
              <span className="detail text-danger">{t('adult')} {numberFormat(booking.additional_guest_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.additional_baby_qty, true)} {t('persons')}</span>
            </Col>}
            {!isCamping && addtiionalPet && <Col xs={12}>
              <span className="title">{t('pet')} ({t('additional')}) : </span>
              <span className="detail text-danger">{transformFieldWithLang(addtiionalPet.pet_type, 'name', lang)} {t('qty')} {numberFormat(addtiionalPet.qty, true)} {t('pet_unit')}</span>
            </Col>}
            {isCamping && <Col xs={12}>
              <span className="title">{t('guest')} : </span>
              <span className="detail">{t('adult')} {numberFormat(booking.guest_qty, true)} {t('persons')}, {t('child', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.guest_child_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.guest_baby_qty, true)} {t('persons')}{booking.tent_qty > 0 ? `, ${t('tent_qty')} ${numberFormat(booking.tent_qty, true)} ${t('tent_unit')}` : ''}</span>
            </Col>}
            {isCamping && <Col xs={12}>
              <span className="title">{t('guest')} ({t('additional')}) : </span>
              <span className="detail">{t('adult')} {numberFormat(booking.extra_person_qty, true)} {t('persons')}, {t('child', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.extra_child_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(booking.extra_baby_qty, true)} {t('persons')}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('contact_name')} : </span>
              <span className="detail">{booking.contact_name}</span>
            </Col>
            {!!hotel && hotel.request_customer_tax_info_enabled && <Col xs={12}>
              <span className="title">{t('customer_tax_id')} : </span>
              <span className="detail">{booking.customer_tax_id}</span>
            </Col>}
            {!!hotel && hotel.request_customer_tax_info_enabled && <Col xs={12}>
              <span className="title">{t('tax_invoice_address')} : </span>
              <span className="detail">{booking.customer_address || '-'}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('telephone')} : </span>
              <span className="detail">{booking.contact_tel_1} {!!booking.contact_tel_2 ? `/ ${booking.contact_tel_2}` : ''}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('email')} : </span>
              <span className="detail">{booking.contact_email || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('line_id')} : </span>
              <span className="detail">{booking.contact_line_id || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('car_model')} : </span>
              <span className="detail">{booking.customer_car_model || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('car_license_plate')} : </span>
              <span className="detail">{booking.customer_car_license_plate || '-'}</span>
            </Col>
          </Row>
        </Col>
        <Col xs={12} md={6}>
          <hr className="my-2  d-block d-md-none" style={{ color: 'darkgray' }} />
          <Row>
            <Col xs={12}>
              <span className="title">{t('payment_methods')} : </span>
              {booking.payment_type === 'payonstay' && <span className="badge bg-danger">{t('payment_method_pay_on_stay')}</span>}
              {booking.payment_type === 'deposit' && <span className="badge bg-danger">{t('payment_method_deposit')}</span>}
              {booking.payment_type === 'full' && <span className="badge bg-success">{t('payment_method_full')}</span>}
              {(!!roomDeposit || booking.room_deposit_outstanding > 0) && <span className="badge bg-secondary ms-1">{t('has_security_deposit')}</span>}
            </Col>
            {!!roomDeposit && <Col xs={12}>
              <span className="title">{t('security_deposit')} : </span>
              {roomDeposit.state === 'refunded'
                ? <span className="detail">{numberFormat(roomDeposit.deposit_amount)} {t('currency_unit')} ({t('deposit_refunded')} {numberFormat(roomDeposit.refund_amount)} {t('currency_unit')}{Number(roomDeposit.expenses_total_price || 0) > 0 && `, ${t('deposit_damage_deducted')} ${numberFormat(roomDeposit.expenses_total_price)} ${t('currency_unit')}`})</span>
                : roomDeposit.state === 'waiting_approve'
                  ? <span className="detail text-warning">{numberFormat(roomDeposit.deposit_amount)} {t('currency_unit')} ({t('deposit_pending_verification')})</span>
                  : <span className="detail text-success">{numberFormat(roomDeposit.deposit_amount)} {t('currency_unit')} ({t('paid')})</span>}
            </Col>}
            {booking.room_deposit_outstanding > 0 && <Col xs={12}>
              <span className="title">{t('security_deposit')} : </span>
              <span className="detail text-danger">{numberFormat(booking.room_deposit_outstanding)} {t('currency_unit')} ({t('please_pay_on_stay')})</span>
            </Col>}
          </Row>
          <hr className="my-2" style={{ color: 'darkgray' }} />
          <Row>
            <Col xs={12}>
              <span className="title">{t('room_price')} : </span>
              <span className="detail">{numberFormat(booking.total_price)} {t('currency_unit')} {(items?.length > 0 && (booking.room_id || booking.room_flexible_id || booking.camp_zone_item_id) && !isCloseAll) && <small className="text-muted">- {bookingDatesStr}</small>}</span>
            </Col>
            {additionalPrice > 0 && <Col xs={12}>
              <span className="title">{t('guest_additional_price')} : </span>
              <span className="detail">{numberFormat(additionalPrice)} {t('currency_unit')}</span>
            </Col>}
            {addtiionalPet && <Col xs={12}>
              <span className="title">{t('pet_additional_price')} : </span>
              <span className="detail">{numberFormat(addtiionalPet.total_price)} {t('currency_unit')}</span>
            </Col>}
            {addonsSum > 0 && <Col xs={12}>
              <span className="title">{t('addons_price')} : </span>
              <span className="detail">{numberFormat(addonsSum)} {t('currency_unit')}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('discount')} : </span>
              <span className="detail">{numberFormat(booking.total_discount)} {t('currency_unit')}</span>
            </Col>

            <Col xs={12}>
              <span className="title">{t('room_charge_paid')} : </span>
              <span className="detail text-success">{numberFormat(booking.paid_amount)} {t('currency_unit')}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('room_charge_outstanding')} : </span>
              <span className={`detail ${outstanding > 0 ? 'text-danger' : ''}`}>{numberFormat(outstanding)} {t('currency_unit')} {outstanding > 0 && <small className="text-warning"> *{t('please_settle_outstanding_on_stay')}</small>}</span>
            </Col>
          </Row>
          <hr className="my-2" style={{ color: 'darkgray' }} />
          <Row>
            {!!roomDeposit && <Col xs={12}>
              <span className="title">{t('net_payment_amount')} : </span>
              <span className="badge bg-success">{numberFormat(Number(booking.paid_amount || 0) + roomDepositPaid)} {t('currency_unit')}</span>
            </Col>}
            {booking.room_deposit_outstanding > 0 && <Col xs={12}>
              <span className="title">{t('pay_amount_on_stay')} : </span>
              <span className="badge bg-danger">{numberFormat(Number(outstanding || 0) + Number(booking.room_deposit_outstanding || 0))} {t('currency_unit')}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('payment_status')} : </span>
              {booking.payment_state === 'waiting' && <span className="badge bg-warning">{t('payment_status_waiting_approve')}</span>}
              {booking.payment_state === 'waiting-approved' && <span className="badge bg-danger">{t('payment_status_pay_on_stay')}</span>}
              {booking.payment_state === 'complete' && <span className="badge bg-success">{t('payment_status_complete')}</span>}
              {booking.payment_state === 'partial' && <span className="badge bg-info">{t('payment_status_deposit_complete')}</span>}
            </Col>
          </Row>
          <div>
            <span className="title">{t('note')} : </span>
            <span className="detail">{booking.booking_note || '-'}</span>
          </div>
          {(addons && addons.length > 0) && <div>
            <span className="title">{t('additional_equipment')} : </span>
            <span className="detail">{addonsTextStr || '-'}  {t('total_price')} {numberFormat(addonsSum)} {t('currency_unit')}</span>
          </div>}
          {booking?.freeAddon && <div>
            <span className="title">{t('free_addons')} : </span>
            <span className="detail">{transformFieldWithLang(booking.freeAddon, 'name', lang) || '-'}</span>
          </div>}
          {!!booking?.food_menu_name && <div>
            <span className="title">{t('food_menu')} : </span>
            <span className="detail">{(lang === 'en' && booking.food_menu_name_en) || booking.food_menu_name}</span>
          </div>}
        </Col>
      </Row>
    )
  }

  const bookingOverviewTabs = () => {
    return (
      <Tab.Container defaultActiveKey="overview">
        <Nav variant="tabs" defaultActiveKey="overview" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="overview">{t('booking_detail_title')}</Nav.Link>
          </Nav.Item>
          {outstanding > 0 && <Nav.Item>
            <Nav.Link eventKey="payment">{t('payment_methods')}</Nav.Link>
          </Nav.Item>}
          {!!hotelPolicy && <Nav.Item>
            <Nav.Link eventKey="terms">{transformFieldWithLang(hotelPolicy, 'title', lang) || t('hotel_policy')}</Nav.Link>
          </Nav.Item>}
          {!!roomPolicy && <Nav.Item>
            <Nav.Link eventKey="room_terms">{transformFieldWithLang(roomPolicy, 'title', lang) || t('room_policy')}</Nav.Link>
          </Nav.Item>}
          {!!campingPolicy && <Nav.Item>
            <Nav.Link eventKey="camping_terms">{transformFieldWithLang(campingPolicy, 'title', lang) || t('camping_policy')}</Nav.Link>
          </Nav.Item>}
          {!!closeAllPolicy && <Nav.Item>
            <Nav.Link eventKey="closeall_terms">{transformFieldWithLang(closeAllPolicy, 'title', lang) || t('closeall_booking_policy')}</Nav.Link>
          </Nav.Item>}
          {foods.length > 0 && <Nav.Item>
            <Nav.Link eventKey="foods">{t('food_suggestion')}</Nav.Link>
          </Nav.Item>}
          {attractions.length > 0 && <Nav.Item>
            <Nav.Link eventKey="attractions">{t('attractions_suggestion')}</Nav.Link>
          </Nav.Item>}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="overview">
            {bookingOverviewSection()}
          </Tab.Pane>
          {outstanding > 0 && <Tab.Pane eventKey="payment" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(hotel, 'payment_info', lang) || `<p>${t('please_contact_admin_for_request_payment_methods')}</p>` }} />
          </Tab.Pane>}
          {!!hotelPolicy && <Tab.Pane eventKey="terms" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(hotelPolicy, 'detail', lang) }} />
          </Tab.Pane>}
          {!!roomPolicy && <Tab.Pane eventKey="room_terms" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(roomPolicy, 'detail', lang) }} />
          </Tab.Pane>}
          {!!campingPolicy && <Tab.Pane eventKey="camping_terms" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(campingPolicy, 'detail', lang) }} />
          </Tab.Pane>}
          {!!closeAllPolicy && <Tab.Pane eventKey="closeall_terms" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(closeAllPolicy, 'detail', lang) }} />
          </Tab.Pane>}
          {foods.length > 0 && <Tab.Pane eventKey="foods">
            <Row>
              {foods.map((item, _i) => (
                <Col key={item.id} xs={12} lg={6} xl={4}>
                  <div className="d-block d-sm-flex align-items-center mt-2">
                    {!!item.image_url && <img className="rounded" style={{ width: 150 }} src={item.image_url} alt="" />}
                    {!item.image_url && <div className="text-center p-2 border rounded">
                      <i className="fal fa-images text-muted" style={{ fontSize: 20 }}></i>
                      <div><small className=" text-muted">{t('no_image')}</small></div>
                    </div>}
                    <div className="ms-0 ms-sm-3 mt-2 mb-3 my-md-0">
                      <h5 className="m-0">{transformFieldWithLang(item, 'name', lang)}</h5>
                      {!!transformFieldWithLang(item, 'description', lang) && <div className="mb-0">{transformFieldWithLang(item, 'description', lang)}</div>}
                      {!!item.tel && <div className="mt-1 text-black">
                        <a href={`tel:${item.tel}`}>
                          <i className="fas fa-phone-alt"></i> {item.tel}
                        </a>
                      </div>}
                      {!!item.line_url && <div className="mt-1 text-success">
                        <a href={item.line_url} target="_blank" rel="noreferrer">
                          <i className="fab fa-line"></i> {t('contact_via_line')}
                        </a>
                      </div>}
                      {!!item.google_map_url && <div className="mt-1 text-primary">
                        <a href={item.google_map_url} target="_blank" rel="noreferrer">
                          <i className="fas fa-map-marker-alt"></i> {t('view_map')}
                        </a>
                      </div>}
                      {!!item.place_link && <div className="mt-1 text-secondary">
                        <a href={item.place_link} target="_blank" rel="noreferrer">
                          <i className="fas fa-globe"></i> {t('website')}
                        </a>
                      </div>}
                      {!!transformFieldWithLang(item, 'detail', lang) && <div className="mt-1 text-secondary">
                        <Button variant="secondary" className="btn-sm mt-0" onClick={() => {
                          setPlaceHtmlDataForShow(transformFieldWithLang(item, 'detail', lang))
                        }}>{t('see_detail_more')}</Button>
                      </div>}
                      <hr className="d-block d-sm-none" style={{ color: 'darkgray' }} />
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab.Pane>}
          {attractions.length > 0 && <Tab.Pane eventKey="attractions">
            <Row>
              {attractions.map((item, _i) => (
                <Col key={item.id} xs={12} lg={6} xl={4}>
                  <div className="d-block d-sm-flex align-items-center mt-2">
                    {!!item.image_url && <img className="rounded" style={{ width: 80 }} src={item.image_url} alt="" />}
                    {!item.image_url && <div className="text-center p-2 border rounded">
                      <i className="fal fa-images text-muted" style={{ fontSize: 20 }}></i>
                      <div><small className=" text-muted">{t('no_image')}</small></div>
                    </div>}
                    <div className="ms-0 ms-sm-3 mt-2 mb-3 my-md-0">
                      <h5 className="m-0">{transformFieldWithLang(item, 'name', lang)}</h5>
                      {!!transformFieldWithLang(item, 'description', lang) && <div className="mb-0">{transformFieldWithLang(item, 'description', lang)}</div>}
                      {!!item.tel && <div className="mt-1 text-black">
                        <a href={`tel:${item.tel}`}>
                          <i className="fas fa-phone-alt"></i> {item.tel}
                        </a>
                      </div>}
                      {!!item.google_map_url && <div className="mt-1 text-success">
                        <a href={item.google_map_url} target="_blank" rel="noreferrer">
                          <i className="fas fa-map-marker-alt"></i> {t('view_map')}
                        </a>
                      </div>}
                      {!!item.place_link && <div className="mt-1 text-secondary">
                        <a href={item.place_link} target="_blank" rel="noreferrer">
                          <i className="fas fa-info-circle"></i> {t('see_detail_more')}
                        </a>
                      </div>}
                      <hr className="d-block d-sm-none" style={{ color: 'darkgray' }} />
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab.Pane>}
        </Tab.Content>
      </Tab.Container>
    )
  }

  return (
    <>
      {/* <Menu header_style_2={true} /> */}
      <div className="text-center">
        {!id && <div className=" px-5 py-3">
          <Row className="booking-form-input">
            <Col xs={6} className="ps-5 pe-2">
              <div className="text-start">
                <div>{t('booking_no')}</div>
                <input
                  type="text"
                  placeholder={t('specify_booking_no')}
                  value={filterCode}
                  onKeyPress={inputKeyPressOnlyNumber}
                  onChange={(e) => setFilterCode(e.target.value)}
                />
              </div>

            </Col>
            <Col xs={6} className="pe-5 ps-2">
              <div className="text-start">
                <div>{t('telephone')}</div>
                <input
                  type="text"
                  placeholder={t('specify_telephone')}
                  value={filterTel}
                  onKeyPress={inputKeyPressOnlyNumber}
                  onChange={(e) => setFilterTel(e.target.value)}
                />
              </div>
            </Col>
          </Row>
          <div className="text-warning text-start ps-5 mt-2">
            <Button variant="dark" className="rounded-0 px-4 me-3" size="md" onClick={() => {
              if (!filterCode || !filterTel) {
                toast.warn(t('please_specify_booking_no_and_telephone'))
                return
              }

              fetchBookingData()
            }}> <i className="far fa-search"></i> {t('search')}</Button>
            <span>** {t('please_specify_booking_no_and_telephone')}</span>
          </div>
        </div>}
        {!booking?.id && <div className="p-5">
          <h4>{t('booking_not_found')}</h4>
          <div>{!id ? t('please_check_booking_no_and_telephone') : t('please_check_link_correct_or_contact_admin')}</div>
        </div>}

        {!!booking?.id && <div className="p-5 pt-2 booking-status">
          <div className="text-end py-2">
            <ChangeLanguageDropdown />
          </div>
          <p className="text-success text-start text-md-center">{warningMsg}</p>
          <h4>{t('booking_detail')}</h4>
          <div className="text-start px-2 px-md-5 py-3">
            {/* โลโก้กับชื่อที่พักกดกลับหน้าแรกได้ -- เป็นที่ที่คนคุ้นว่ากดแล้วกลับบ้าน
              * แต่ไม่พอเพราะไม่มีอะไรบอกว่ากดได้ จึงมีปุ่มชัดๆ ท้ายหน้าด้วย */}
            <Link
              to={`/${hotel?.code || ''}`}
              className="d-flex align-items-center text-reset text-decoration-none"
              title={t('back_to_hotel_home')}
            >
              {!!hotel?.logo_file_path && <img src={hotel?.logo_file_path} className="hotel-logo" alt="img" />}
              <h5 className="mx-2 mb-0">{transformFieldWithLang(hotel, 'name', lang)}</h5>
            </Link>
            {/* ไว้นอกแท็บ -- แท็บแสดงเฉพาะที่พักที่เปิด show_hotel_info_to_customer
              * ปุ่มจ่ายเงินต้องเห็นเสมอ ไม่งั้นลิงก์จาก SMS / QR พาลูกค้ามาแล้วหาปุ่มไม่เจอ */}
            <div className="mt-3">
              <BalancePayCard
                type={booking.is_close_zone && booking.main_id ? 'main' : 'booking'}
                id={booking.is_close_zone && booking.main_id ? booking.main_id : booking.id}
                code={booking.booking_no}
              />
            </div>
            {hotel?.show_hotel_info_to_customer && <div>
              {bookingOverviewTabs()}
            </div>}
            {!hotel?.show_hotel_info_to_customer && <div>
              {bookingOverviewSection()}
            </div>}
          </div>

          {/* อ่านจบแล้วต้องมีทางไปต่อ -- ก่อนหน้านี้หน้านี้เป็นทางตัน
            * ลูกค้าที่อยากจองเพิ่มหรือดูห้องอื่นต้องกดย้อนกลับหรือพิมพ์ลิงก์เอง */}
          {!!hotel?.code && (
            <div className="text-center pt-2">
              <Link to={`/${hotel.code}`} className="btn btn-outline-dark px-4">
                {t('back_to_hotel_home')}
              </Link>
            </div>
          )}
        </div>}
      </div >
      <Modal size="lg" show={!!placeHtmlDataForShow} onHide={() => setPlaceHtmlDataForShow(null)} centered>
        <Modal.Body className="booking-policy">
          <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: placeHtmlDataForShow }} />
        </Modal.Body>
      </Modal>
      <OnceFooter />
    </>
  );
}
